'use client';
import React, { forwardRef, InputHTMLAttributes, useState, KeyboardEvent, useEffect } from 'react';
import { cn } from '../../utils/cn';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  rightIcon?: React.ReactNode;
  error?: boolean | string;
  onEnter?: () => void;
  label?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      placeholder,
      value,
      defaultValue,
      rightIcon,
      name,
      type = 'text',
      error,
      onEnter,
      required = false,
      disabled,
      label,
      ...rest
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isTouched, setIsTouched] = useState(false);
    const [hasInputValue, setHasInputValue] = useState(false);

    // Check for initial value (controlled or uncontrolled)
    useEffect(() => {
      const hasInitialValue = !!(defaultValue || value);
      setHasInputValue(hasInitialValue);
    }, [defaultValue, value]);

    // Update hasInputValue when controlled value changes
    useEffect(() => {
      if (value !== undefined) {
        setHasInputValue(value !== '');
      }
    }, [value]);

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && onEnter) {
        onEnter();
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setHasInputValue(e.target.value !== '');
      rest.onChange?.(e);
    };

    const displayLabel = label || placeholder;
    const appendAsterisk = required && displayLabel ? `${displayLabel}*` : displayLabel;

    const showFloatingLabel = isFocused || hasInputValue || type === 'date';
    const showError = !!error || (required && isTouched && !hasInputValue);

    return (
      <div className="w-full">
        <div
          className={cn(
            'relative flex min-h-[64px] border border-solid items-center transition-colors',
            'focus-within:border-velux-splash-blue',
            showError ? 'border-velux-red' : 'border-velux-light-grey',
            disabled && 'bg-velux-background-grey text-velux-grey',
            className
          )}
        >
          {appendAsterisk && (
            <label
              className={cn(
                'absolute text-velux-grey left-4 pointer-events-none transition-all duration-200',
                showFloatingLabel ? 'top-3 text-sm' : 'top-5 text-base lg:text-md',
                disabled && 'text-velux-grey'
              )}
              htmlFor={name}
            >
              {appendAsterisk}
            </label>
          )}
          <input
            ref={ref}
            type={type}
            value={value}
            defaultValue={defaultValue}
            className={cn(
              'w-full px-4 outline-none border-0  bg-transparent',
              appendAsterisk ? 'pt-6 pb-2' : 'py-4',
              disabled && 'text-velux-grey cursor-not-allowed'
            )}
            id={name}
            name={name}
            disabled={disabled}
            placeholder=""
            onKeyDown={handleKeyDown}
            onChange={handleInputChange}
            onMouseDown={() => setIsTouched(true)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);
              setIsTouched(true);
            }}
            {...rest}
          />
          {rightIcon && <div className="p-4 cursor-pointer">{rightIcon}</div>}
        </div>
        {typeof error === 'string' && error && (
          <small className="block mt-1 pl-4 text-velux-red text-xs lg:text-sm">{error}</small>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
